/**
 * 포스트 표(`PostTable`)가 칸에 그리는 지표 값과 머리 정렬 — 표 안 정렬과 바깥이 쥔 정렬(`sortControl`)이 같은 셈을 탄다.
 *
 * 칸 값은 «가장 최근 날짜 스냅샷(`metrics.YYYYMMDD`) → 점수 시각 스냅샷 → 문서 필드» 순서로 읽는다.
 * 카드·합계가 읽는 `getMetricsByScoreTime` 과 다를 수 있다(최근 스냅샷이 점수 시각보다 뒤면).
 */

import { getMetricAtDay } from 'src/utils/metrics/metric-day-detail';
import { engagementRatePct } from 'src/utils/metrics/engagement-rate';
import { getPopularityScore } from 'src/utils/metrics/popularity-score';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';

/** 칸에 보이는 지표 여섯(숫자) */
export type PostTableRawMetrics = {
  like: number;
  comment: number;
  view: number;
  saved: number;
  share: number;
  download: number;
};

/** 정렬 방향 */
export type PostTableSortDirection = 'asc' | 'desc';

/** 머리 정렬이 읽는 셈 옵션 — 캠페인 화면은 정렬 스냅샷(`metricsSort`·`metricScoreSort`)을 끈다 */
export type PostTableSortOptions = {
  metricsByScoreOpts?: Record<string, unknown>;
  popularityScoreOpts?: Record<string, unknown>;
  /** 지표 없는 일차 머리 값(`viewDay7`)이 읽는 지표 키(`viewCount` 등) */
  dayMetricKey?: string;
};

/** 일차 열 머리 값의 일차 — 1·3·7·30 */
const DAY_SORT_DAYS: ReadonlySet<number> = new Set([1, 3, 7, 30]);

/**
 * 일차 열의 머리 값 — 지표를 주면 `viewDay7:likeCount`(지표마다 일차 열을 세운 표), 안 주면 `viewDay7`(표가 읽는 지표 하나 · `dayMetricKey`).
 * @param day 1 · 3 · 7 · 30
 * @param metricKey `viewCount` · `likeCount` …
 */
export function dayMetricSortField(day: number, metricKey?: string): string {
  return metricKey ? `viewDay${day}:${metricKey}` : `viewDay${day}`;
}

/**
 * 머리 값이 일차 열이면 그 일차와 지표.
 * @returns 일차 열이 아니면 `null` · 지표가 없는 값(`viewDay7`)이면 `metricKey` 가 없다
 */
export function parseDayMetricSortField(field: string | null | undefined): { day: number; metricKey?: string } | null {
  const m = /^viewDay(\d+)(?::([A-Za-z]+))?$/.exec(String(field ?? ''));
  if (!m || !DAY_SORT_DAYS.has(Number(m[1]))) return null;
  return m[2] ? { day: Number(m[1]), metricKey: m[2] } : { day: Number(m[1]) };
}

/**
 * 초·밀리초 epoch · 8자리 `YYYYMMDD`(숫자·문자열) · ISO/Date → epoch ms.
 * @returns 읽을 수 없으면 `null`
 */
export function normalizePostTimestampMs(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') {
    const abs = Math.abs(raw);
    if (abs >= 10_000_000 && abs <= 99_999_999) {
      const year = Math.floor(raw / 10_000);
      const month = Math.floor((abs % 10_000) / 100) - 1;
      const day = abs % 100;
      const d = new Date(year, month, day);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
    return abs < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    return normalizePostTimestampMs(Number(raw.trim()));
  }
  if (typeof raw === 'string' && /^\d{8}$/.test(raw.trim())) {
    const s = raw.trim();
    const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const d = raw instanceof Date ? raw : new Date(raw as string);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * `metrics` 의 가장 최근 날짜 키(`YYYYMMDD`) 스냅샷.
 * @returns 날짜 키가 없으면 `null`
 */
export function getLatestDateMetrics(post: any): Record<string, any> | null {
  if (!post?.metrics || typeof post.metrics !== 'object') return null;
  const dateKeys = Object.keys(post.metrics).filter((key) => /^\d{8}$/.test(key));
  if (dateKeys.length === 0) return null;
  const latestKey = dateKeys.reduce((latest, current) => (parseInt(current, 10) > parseInt(latest, 10) ? current : latest));
  return post.metrics[latestKey] || null;
}

/** 표 칸의 지표 값(숫자) — 숫자가 아니면 0 */
export function readPostTableRawMetrics(post: any, metricsByScoreOpts: Record<string, unknown> = {}): PostTableRawMetrics {
  const latest = getLatestDateMetrics(post);
  const fallback: any = getMetricsByScoreTime(post, metricsByScoreOpts);
  const num = (value: unknown) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    like: num(latest?.likeCount ?? fallback?.likeCount ?? post?.likeCount ?? post?.likes ?? 0),
    comment: num(latest?.commentCount ?? fallback?.commentCount ?? post?.commentCount ?? post?.comments ?? 0),
    view: num(latest?.viewCount ?? fallback?.viewCount ?? post?.viewCount ?? post?.views ?? post?.impressions ?? 0),
    saved: num(latest?.savedCount ?? fallback?.savedCount ?? post?.savedCount ?? 0),
    share: num(latest?.shareCount ?? fallback?.shareCount ?? post?.shareCount ?? 0),
    download: num(latest?.downloadCount ?? fallback?.downloadCount ?? post?.downloadCount ?? 0),
  };
}

/**
 * 머리 값으로 글을 정렬한다 — `account` · `date` · `engagement` · `view` · 일차 열(`dayMetricSortField`) · `engagementRate` ·
 * 반응 열(`like` · `comment` · `saved` · `share` · `download`). 모르는 머리는 받은 순서 그대로.
 * E/R 을 셀 수 없는 글(조회 0)은 방향과 상관없이 맨 뒤다. 같은 값은 받은 순서를 지킨다.
 * @returns 새 배열
 */
export function sortPostsForTable<T>(
  posts: readonly T[],
  field: string | null | undefined,
  direction: PostTableSortDirection,
  options: PostTableSortOptions = {},
): T[] {
  const list = Array.isArray(posts) ? [...posts] : [];
  if (!field) return list;
  const { metricsByScoreOpts = {}, popularityScoreOpts = {}, dayMetricKey = 'viewCount' } = options;
  const dayField = parseDayMetricSortField(field);

  return list.sort((a: any, b: any) => {
    let aValue: any;
    let bValue: any;
    if (dayField) {
      const metricKey = dayField.metricKey ?? dayMetricKey;
      aValue = getMetricAtDay(a, dayField.day, metricKey) ?? -1;
      bValue = getMetricAtDay(b, dayField.day, metricKey) ?? -1;
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    }
    switch (field) {
      case 'account':
        aValue = String(a?.account || a?.username || a?.author || '').toLowerCase();
        bValue = String(b?.account || b?.username || b?.author || '').toLowerCase();
        break;
      case 'date':
        aValue = normalizePostTimestampMs(a?.timestamp ?? a?.postDate ?? a?.date) ?? 0;
        bValue = normalizePostTimestampMs(b?.timestamp ?? b?.postDate ?? b?.date) ?? 0;
        break;
      case 'engagement':
        aValue = getPopularityScore(a, popularityScoreOpts);
        bValue = getPopularityScore(b, popularityScoreOpts);
        break;
      case 'view':
        aValue = readPostTableRawMetrics(a, metricsByScoreOpts).view || 0;
        bValue = readPostTableRawMetrics(b, metricsByScoreOpts).view || 0;
        break;
      case 'engagementRate': {
        const ra = readPostTableRawMetrics(a, metricsByScoreOpts);
        const rb = readPostTableRawMetrics(b, metricsByScoreOpts);
        aValue = engagementRatePct(ra.like, ra.comment, ra.view);
        bValue = engagementRatePct(rb.like, rb.comment, rb.view);
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        break;
      }
      case 'like':
      case 'comment':
      case 'saved':
      case 'share':
      case 'download':
        aValue = readPostTableRawMetrics(a, metricsByScoreOpts)[field] || 0;
        bValue = readPostTableRawMetrics(b, metricsByScoreOpts)[field] || 0;
        break;
      default:
        return 0;
    }
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}
